import React from 'react';
import { View, Modal, TouchableOpacity, ActivityIndicator } from 'react-native';
import Text from '@components/CustomText';
import Svg, { Circle, Path } from 'react-native-svg';
import { ScaledSheet } from '@utils/scale';
import { useNavigation } from '@react-navigation/native';

type TransactionStatus = 'processing' | 'success' | 'failed' | 'pending' | 'insufficient_funds';

interface TransactionData {
  status: TransactionStatus;
  amount?: number;
  title?: string;
  subtitle?: string;
  bulletPoints?: string[];
}

interface TransactionStatusModalProps {
  visible: boolean;
  data: TransactionData | null;
  onClose: () => void;
}

const TransactionStatusModal: React.FC<TransactionStatusModalProps> = ({
  visible,
  data,
  onClose,
}) => {
  const navigation = useNavigation();
  if (!data) return null;

  const { status, amount, title, subtitle, bulletPoints } = data;

  const renderIcon = () => {
    switch (status) {
      case 'processing':
        return (
          <View style={styles.iconContainer}>
            <ActivityIndicator size="large" color="#4A90E2" />
          </View>
        );

      case 'success':
        return (
          <View style={[styles.iconContainer, styles.successIconContainer]}>
            <View style={styles.successIconOuter}>
              <View style={styles.successIconInner}>
                <Svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <Path
                    d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
                    fill="#FFFFFF"
                  />
                </Svg>
              </View>
            </View>
          </View>
        );

      case 'failed':
        return (
          <View style={[styles.iconContainer, styles.failedIconContainer]}>
            <View style={styles.failedIconOuter}>
              <View style={styles.failedIconInner}>
                <Svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <Path
                    d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"
                    fill="#FFFFFF"
                  />
                </Svg>
              </View>
            </View>
          </View>
        );

      case 'pending':
        return (
          <View style={[styles.iconContainer, styles.pendingIconContainer]}>
            <View style={styles.pendingIconOuter}>
              <View style={styles.pendingIconInner}>
                <Svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <Circle cx="12" cy="12" r="10" stroke="#FFFFFF" strokeWidth="2" fill="none" />
                  <Path
                    d="M12 6v6l4 2"
                    stroke="#FFFFFF"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </Svg>
              </View>
            </View>
          </View>
        );

      default:
        return null;
    }
  };

  const renderContent = () => {
    if (status === 'insufficient_funds') {
      return (
        <>
          {title && <Text style={styles.insufficientTitle}>{title}</Text>}
          {bulletPoints && bulletPoints.length > 0 && (
            <View style={styles.bulletContainer}>
              {bulletPoints.map((point: string, index: number) => (
                <Text key={index} style={styles.bulletPoint}>• {point}</Text>
              ))}
            </View>
          )}
        </>
      );
    }

    return (
      <>
        {title && <Text style={styles.title}>{title}</Text>}
        {amount !== undefined && <Text style={styles.amount}>₹ {amount}</Text>}
        {subtitle && <Text style={styles.subtitle}>{subtitle}</Text>}
      </>
    );
  };

  const showCloseButton = status !== 'processing';

  return (
    <Modal
      statusBarTranslucent
      navigationBarTranslucent
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          {showCloseButton && (
            <TouchableOpacity style={styles.closeButton} onPress={onClose}>
              <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <Path
                  d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"
                  fill="#666666"
                />
              </Svg>
            </TouchableOpacity>
          )}

          {status !== 'insufficient_funds' && (
            <View style={styles.iconWrapper}>
              {renderIcon()}
            </View>
          )}

          <View style={styles.contentContainer}>
            {renderContent()}
            {status === "pending" && (
              <TouchableOpacity
                activeOpacity={0.8}
                style={styles.viewStatementButton}
                onPress={() => {
                  onClose();
                  navigation.pop();
                  navigation.replace("TransactionStatements", {
                    type: "WITHDRAWAL",
                  });
                }}
              >
                <Text style={styles.viewStatementButtonText}>
                  View Statement
                </Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = ScaledSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContainer: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    paddingVertical: 40,
    paddingHorizontal: 24,
    minHeight: 300,
    alignItems: 'center',
  },
  closeButton: {
    position: 'absolute',
    top: 16,
    right: 16,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#F5F5F5',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  iconWrapper: {
    marginBottom: 32,
  },
  iconContainer: {
    width: 120,
    height: 120,
    justifyContent: 'center',
    alignItems: 'center',
  },
  successIconContainer: {
    // Success styling handled by nested views
  },
  successIconOuter: {
    width: 120,
    height: 120,
    borderRadius: 60,
    backgroundColor: 'rgba(76, 217, 100, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  successIconInner: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#4CD964',
    justifyContent: 'center',
    alignItems: 'center',
  },
  failedIconContainer: {
    // Failed styling handled by nested views
  },
  failedIconOuter: {
    width: 120,
    height: 120,
    borderRadius: 60,
    backgroundColor: 'rgba(255, 59, 48, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  failedIconInner: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#FF3B30',
    justifyContent: 'center',
    alignItems: 'center',
  },
  pendingIconContainer: {
    // Pending styling handled by nested views
  },
  pendingIconOuter: {
    width: 120,
    height: 120,
    borderRadius: 60,
    backgroundColor: 'rgba(74, 144, 226, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  pendingIconInner: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#4A90E2',
    justifyContent: 'center',
    alignItems: 'center',
  },
  contentContainer: {
    alignItems: 'center',
    width: '100%',
  },
  title: {
    fontSize: 22,
    fontWeight: '600',
    color: '#000000',
    marginBottom: 16,
    textAlign: 'center',
  },
  amount: {
    fontSize: 32,
    fontWeight: '700',
    color: '#000000',
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 14,
    color: '#666666',
    textAlign: 'center',
    marginTop: 8,
  },
  insufficientTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#000000',
    marginBottom: 24,
    textAlign: 'left',
    width: '100%',
  },
  bulletContainer: {
    width: '100%',
    paddingHorizontal: 8,
  },
  bulletPoint: {
    fontSize: 14,
    color: '#333333',
    lineHeight: 24,
    marginBottom: 12,
    textAlign: "left",
  },
  viewStatementButton: {
    marginTop: 24,
    paddingVertical: 12,
    paddingHorizontal: 32,
    borderRadius: 10,
    backgroundColor: "#1D9BF0",
    alignSelf: "center",
  },
  viewStatementButtonText: {
    fontSize: 16,
    fontWeight: "600",
    color: "#FFFFFF",
  },
});

export default TransactionStatusModal;